import { Popover } from '@mui/material';
import React, { FC, useState } from 'react';
import { TextField, TextFieldProps } from '../textField';
import { Calendar } from '../datePicker/icons';
import { useTranslation } from 'react-i18next';
import { cn } from '../../../../util';
import { Button } from '../../button';
import Util from '../../../../../misc/util';

type Props = {
  onChange: (months: number[]) => void;
  label?: string;
  initialValue?: number[];
  placeholder?: string;
  helperText?: TextFieldProps['helperText'];
  required?: TextFieldProps['required'];
  disabled?: boolean;
  error?: TextFieldProps['error'];
  multiSelect?: boolean;
};

const MONTHS = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12];

export const MonthPicker: FC<Props> = ({
  onChange,
  label,
  initialValue = [],
  placeholder,
  helperText,
  required,
  disabled,
  error,
  multiSelect = true
}) => {
  const { t } = useTranslation();
  const [selectedMonths, setSelectedMonths] = useState<number[]>(initialValue);
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

  const handleOpen = (event: React.MouseEvent<HTMLElement>) => {
    if (!disabled) {
      setAnchorEl(event.currentTarget);
    }
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleMonthClick = (monthValue: number) => {
    let newSelection: number[];

    if (multiSelect) {
      if (selectedMonths.includes(monthValue)) {
        newSelection = selectedMonths.filter((m) => m !== monthValue);
      } else {
        newSelection = [...selectedMonths, monthValue].sort((a, b) => a - b);
      }
    } else {
      newSelection = [monthValue];
    }

    setSelectedMonths(newSelection);
  };

  const handleClear = () => {
    setSelectedMonths([]);
    onChange?.([]);
  };

  const handleSelectAll = () => {
    const allMonths = MONTHS;
    setSelectedMonths(allMonths);
  };

  const handleApply = () => {
    onChange?.(selectedMonths);
    handleClose();
  };

  const getDisplayValue = () => {
    if (selectedMonths.length === 0) return '';

    if (selectedMonths.length === 12) {
      return t('common:allMonths');
    }

    return selectedMonths
      .map((monthValue) => {
        const month = MONTHS.find((m) => m === monthValue);
        return month ? Util.numberToMonthName(month) : '';
      })
      .join(', ');
  };

  const open = Boolean(anchorEl);

  return (
    <>
      <TextField
        label={label}
        value={getDisplayValue()}
        placeholder={placeholder ?? t('common:selectMonth')}
        helperText={error ? helperText : undefined}
        required={required}
        disabled={disabled}
        error={error}
        onClick={handleOpen}
        inputProps={{
          readOnly: true,
          className: 'placeholder:!text-grey-500 placeholder:!opacity-75 cursor-pointer'
        }}
        endAdornment={
          <Calendar
            style={{
              cursor: disabled ? 'default' : 'pointer',
              opacity: disabled ? 0.5 : 1
            }}
          />
        }
      />

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left'
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left'
        }}
        slotProps={{ paper: { className: 'p-6 shadow-500 flex flex-col gap-y-6' } }}
      >
        <div className="flex justify-between items-center h-6">
          <h3 className="font-semibold">{t('common:selectMonth')}</h3>
          <button
            onClick={handleSelectAll}
            className="h-6 text-sm text-grey-600 border-grey-100 border-solid hover:bg-grey-100 hover:border-grey-200 py-y px-2 rounded-lg bg-grey-50 transition-colors cursor-pointer"
          >
            {t('common:all')}
          </button>
        </div>
        <div className="grid grid-cols-3 gap-1">
          {MONTHS.map((month) => {
            const isSelected = selectedMonths.includes(month);
            return (
              <button
                key={month}
                onClick={() => handleMonthClick(month)}
                className={cn(
                  'h-10 w-24 cursor-pointer border-none rounded-lg text-base font-normal transition-all',
                  isSelected
                    ? 'bg-heaven-600 text-white font-medium'
                    : 'bg-transparent hover:bg-grey-50'
                )}
              >
                {Util.numberToMonthName(month, true)}
              </button>
            );
          })}
        </div>

        <div className="h-10 flex gap-2 w-full">
          <Button onClick={handleClear} variant="tertiary" className="w-full">
            {t('common:DELETE')}
          </Button>
          <Button
            onClick={handleApply}
            className="w-full bg-heaven-600 hover:bg-heaven-800 active:bg-heaven-800"
          >
            {t('template:APPLY')}
          </Button>
        </div>
      </Popover>
    </>
  );
};
